import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  useNavigate,
  useOutletContext,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { Award, CheckCircle2, Loader2, RefreshCcw } from 'lucide-react';
import { useApplyToJob, useJobById } from 'src/hooks/useJobsData';
import { ResumeService } from 'src/services/api/ResumeService';
import { Button } from 'src/components/ui/button';
import {
  isNA,
  parseJD,
  renderInline,
  sentenceCase,
} from 'src/pages/SeniorLevelFlow/seniorFlowJd';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { useToast } from 'src/hooks/use-toast';
import { AuthCard } from 'src/components/AuthCard/AuthCard';
import { getToken, isAuthenticated } from 'src/utils/authStorage';

// ── Helpers ──────────────────────────────────────────────────────────────────

const formatDate = (dateValue) => {
  if (!dateValue) return '-';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return dateValue;
  return date.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const humanizeCareerLevel = (raw) => {
  if (!raw) return '-';
  return raw
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const splitMandatoryItems = (items, labelKey) => {
  const safeItems = items || [];
  return {
    mandatory: safeItems.filter((i) => i.is_mandatory).map((i) => i[labelKey]),
    optional: safeItems.filter((i) => !i.is_mandatory).map((i) => i[labelKey]),
  };
};

// API shape: qualifications[] = { qualification_type, field_of_study[] }
// where field_of_study[] = { field_of_study_name, is_mandatory }.
// is_mandatory lives on the inner field entries, not on the qualification.
// Flatten into one display row per field (or one row when no fields given).
const flattenQualifications = (quals) => {
  const rows = [];
  (quals || []).forEach((q, qi) => {
    const type = humanizeCareerLevel(q?.qualification_type);
    const fields = Array.isArray(q?.field_of_study) ? q.field_of_study : [];
    if (fields.length === 0) {
      rows.push({
        key: `${type}-${qi}`,
        label: type,
        mandatory: !!q?.is_mandatory,
      });
    } else {
      fields.forEach((f, fi) => {
        const name = f?.field_of_study_name || '';
        rows.push({
          key: `${type}-${name || fi}-${qi}`,
          label: name ? `${type} — ${name}` : type,
          mandatory: !!f?.is_mandatory,
        });
      });
    }
  });
  return rows;
};

const normalizeAdditionalDetails = (details) => {
  if (!details) return [];
  if (Array.isArray(details)) {
    return details
      .map((item, idx) => {
        if (typeof item === 'object' && item !== null) {
          const label =
            item.label ||
            item.key ||
            item.name ||
            item.title ||
            `Detail ${idx + 1}`;
          const rawValue =
            item.value ?? item.detail ?? item.description ?? item.text;
          const hasKnown =
            item.value !== undefined ||
            item.detail !== undefined ||
            item.description !== undefined ||
            item.text !== undefined;
          if (hasKnown) return { label, value: String(rawValue ?? '-') };
          const [firstKey] = Object.keys(item);
          return {
            label: firstKey || `Detail ${idx + 1}`,
            value: String(firstKey ? item[firstKey] : '-'),
          };
        }
        return { label: `Detail ${idx + 1}`, value: String(item) };
      })
      .filter((e) => e.label);
  }
  if (typeof details === 'object') {
    return Object.entries(details).map(([label, value]) => ({
      label,
      value: String(value ?? '-'),
    }));
  }
  return [{ label: 'Additional Details', value: String(details) }];
};

// ── Flat content section (.jd-section) — no card fill/border, separated by gap ──
function Section({ children, className = '' }) {
  return <section className={className}>{children}</section>;
}

// ── Section heading (.jd-section h2) — plain text, no icon chip ────────────────
function SectionHeading({ children }) {
  return (
    <h2
      className="mb-2 text-[1.02rem] font-bold"
      style={{
        color: 'var(--text)',
        fontFamily: 'var(--font-display)',
        letterSpacing: '-0.01em',
      }}
    >
      {children}
    </h2>
  );
}

// ── Job description markdown rendering ──────────────────────────────────────
// `job_description` comes back as markdown (headings, **bold**, bullet
// lists, `**Key:**` value lines) — reuses the same parser SeniorLevelFlow
// uses for the identical dialect (`seniorFlowJd.parseJD`), restyled with
// this page's own Tailwind/CSS-var look instead of that page's `.slf-*`
// classes. Nothing from the API is ever inserted as raw HTML.
function MarkdownList({ list, listKey }) {
  const Tag = list.ordered ? 'ol' : 'ul';
  return (
    <Tag
      className={`ml-5 space-y-1 text-sm leading-relaxed ${
        list.ordered ? 'list-decimal' : 'list-disc'
      }`}
      style={{ color: 'var(--text-muted)' }}
    >
      {list.items.map((item, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={`${listKey}-${i}`}>
          {renderInline(item.text)}
          {item.children ? (
            <MarkdownList list={item.children} listKey={`${listKey}-${i}`} />
          ) : null}
        </li>
      ))}
    </Tag>
  );
}

function MarkdownValue({ item, itemKey }) {
  if (isNA(item.value)) {
    return <span style={{ color: 'var(--text-faint)' }}>Not specified</span>;
  }
  return (
    <>
      {item.value ? renderInline(item.value) : null}
      {item.list ? <MarkdownList list={item.list} listKey={itemKey} /> : null}
    </>
  );
}

function MarkdownBlock({ block, blockKey }) {
  if (block.type === 'p') {
    return (
      <p
        className="text-sm leading-relaxed"
        style={{ color: 'var(--text-muted)' }}
      >
        {renderInline(block.text)}
      </p>
    );
  }
  if (block.type === 'sub') {
    return (
      <h4
        className="text-sm font-semibold"
        style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}
      >
        {renderInline(block.text)}
      </h4>
    );
  }
  if (block.type === 'list') {
    return <MarkdownList list={block} listKey={blockKey} />;
  }
  if (block.type === 'kv') {
    return (
      <dl className="space-y-1">
        {block.items.map((item, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={`${blockKey}-${i}`} className="text-sm leading-relaxed">
            <dt
              className="inline font-semibold"
              style={{ color: 'var(--text)' }}
            >
              {sentenceCase(item.key)}:{' '}
            </dt>
            <dd className="inline">
              <MarkdownValue item={item} itemKey={`${blockKey}-${i}`} />
            </dd>
          </div>
        ))}
      </dl>
    );
  }
  return null;
}

function JobDescriptionMarkdown({ md }) {
  const sections = useMemo(() => parseJD(md), [md]);
  if (!sections.length) {
    return (
      <p className="text-sm italic" style={{ color: 'var(--text-faint)' }}>
        No description provided.
      </p>
    );
  }
  return (
    <div className="flex flex-col gap-2.5">
      {sections.map((section, si) => (
        // eslint-disable-next-line react/no-array-index-key
        <React.Fragment key={`sec-${si}`}>
          {section.title && (
            <h3
              className="text-[0.95rem] font-bold"
              style={{
                color: 'var(--text)',
                fontFamily: 'var(--font-display)',
                letterSpacing: '-0.01em',
              }}
            >
              {renderInline(section.title)}
            </h3>
          )}
          {section.blocks.map((block, bi) => (
            <MarkdownBlock
              // eslint-disable-next-line react/no-array-index-key
              key={`sec-${si}-b-${bi}`}
              block={block}
              blockKey={`sec-${si}-b-${bi}`}
            />
          ))}
        </React.Fragment>
      ))}
    </div>
  );
}

// ── Apply modal ───────────────────────────────────────────────────────────────
function ApplyModal({ open, onOpenChange, job, onSuccess }) {
  const { toast } = useToast();
  const [searchParams] = useSearchParams();
  const ref = searchParams.get('ref');
  const availableLocations = job?.locations || [];
  const [selectedIds, setSelectedIds] = useState([]);
  const applyMutation = useApplyToJob();
  const isSubmitting = applyMutation.isPending;
  const firstCheckRef = useRef(null);

  // Pre-select first combo on open
  useEffect(() => {
    if (open && availableLocations.length > 0) {
      setSelectedIds([availableLocations[0].requirement_location_id]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Move focus to first checkbox when dialog opens
  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(() => firstCheckRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [open]);

  const toggle = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (!selectedIds.length) return;
    const jobId = job?.job_code;
    if (!jobId) {
      toast({ message: 'Job identifier missing.', variant: 'destructive' });
      return;
    }
    try {
      const payload = { preferred_work_locations: selectedIds };
      if (ref) payload.institution_reference = ref;
      const res = await applyMutation.mutateAsync({ jobId, payload });
      if (res?.success === false)
        throw new Error(res?.message || 'Submission failed');
      toast({
        message: 'Application submitted! Complete your profile.',
        variant: 'success',
      });
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast({
        message: err?.message || 'Unable to submit application.',
        variant: 'destructive',
      });
    }
  };

  const submitLabel =
    selectedIds.length > 1
      ? `Submit ${selectedIds.length} Applications`
      : 'Submit Application';

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isSubmitting) onOpenChange(next);
      }}
    >
      <DialogContent
        className="max-w-[460px] p-0"
        style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
        aria-label={`Apply — ${job?.title || 'Opening'}`}
      >
        <DialogHeader
          className="border-b px-6 py-5"
          style={{ borderColor: 'var(--border-light)' }}
        >
          <DialogTitle
            style={{ color: 'var(--text)', fontFamily: 'var(--font-display)' }}
          >
            Choose Preferred Locations
          </DialogTitle>
          <DialogDescription style={{ color: 'var(--text-faint)' }}>
            Select one or more locations, then submit your application.
          </DialogDescription>
        </DialogHeader>

        <div
          className="max-h-[55dvh] overflow-y-auto px-6 py-4"
          role="group"
          aria-label="Location options"
        >
          {availableLocations.length === 0 && (
            <p
              className="py-6 text-center text-sm"
              style={{ color: 'var(--text-faint)' }}
            >
              No locations listed.
            </p>
          )}
          {availableLocations.map((loc, idx) => {
            const id = loc.requirement_location_id;
            const checked = selectedIds.includes(id);
            const labelId = `apply-loc-${id}`;
            return (
              <div
                key={id}
                role="checkbox"
                aria-checked={checked}
                tabIndex={0}
                id={labelId}
                ref={idx === 0 ? firstCheckRef : undefined}
                onClick={() => toggle(id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggle(id);
                  }
                }}
                className="mb-2 flex cursor-pointer items-center gap-3 rounded-[var(--radius-sm)] border p-3.5 transition-colors last:mb-0 focus-visible:outline-none focus-visible:ring-2"
                style={{
                  borderColor: checked ? 'var(--primary)' : 'var(--border)',
                  background: checked
                    ? 'var(--primary-light)'
                    : 'var(--surface)',
                  '--tw-ring-color': 'var(--ring)',
                }}
              >
                {/* Checkbox indicator */}
                <span
                  className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border transition-colors"
                  style={{
                    borderColor: checked ? 'var(--primary)' : 'var(--border)',
                    background: checked ? 'var(--primary)' : 'transparent',
                  }}
                  aria-hidden="true"
                >
                  {checked && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path
                        d="M1 4l3 3 5-6"
                        stroke="#fff"
                        strokeWidth="1.8"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </span>
                <span
                  className="min-w-0 flex-1 text-sm font-medium"
                  style={{ color: 'var(--text)' }}
                >
                  {loc.location_name}
                  {loc.employment_type && (
                    <span
                      className="ml-1 font-normal"
                      style={{ color: 'var(--text-faint)' }}
                    >
                      · {loc.employment_type}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>

        {/* DialogFooter defaults to flex-col-reverse on mobile (primary action
            on top) — override to flex-col so Cancel/Submit keep the same
            left-to-right order as desktop, just stacked. */}
        <DialogFooter
          className="flex-col border-t px-6 py-4"
          style={{
            borderColor: 'var(--border-light)',
            background: 'var(--surface-muted)',
          }}
        >
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
            className="sm:mr-2"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || selectedIds.length === 0}
            style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}
            className="hover:opacity-90"
          >
            {isSubmitting ? (
              <>
                <Loader2
                  className="mr-2 h-4 w-4 animate-spin"
                  aria-hidden="true"
                />
                Submitting…
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function OpeningDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const outletContext = useOutletContext();
  const { data: response, isLoading, isError, error, refetch } = useJobById(id);

  const job = response?.data ?? null;

  const [isUserAuthenticated, setIsUserAuthenticated] = useState(false);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const [isAuthOverlayOpen, setIsAuthOverlayOpen] = useState(false);
  // True only when the candidate authenticated during *this* Apply click (via
  // the overlay) — as opposed to already having a live session beforehand.
  // Only that first-time path should be gated into account verification
  // afterward; a returning signed-in user applying to another job should
  // never get bounced off to /create-link.
  const freshlyAuthenticatedRef = useRef(false);

  useEffect(() => {
    setIsUserAuthenticated(isAuthenticated());
  }, []);

  // Push dynamic breadcrumbs up to Header via outlet context
  useEffect(() => {
    if (!outletContext?.setHeaderBreadcrumbs || !job?.title) return undefined;
    outletContext.setHeaderBreadcrumbs([
      { label: 'Open Positions', href: '/openings' },
      { label: job.title },
    ]);
    return () => outletContext.setHeaderBreadcrumbs?.(null);
  }, [job?.title, outletContext]);

  const handleApplyClick = () => {
    if (isUserAuthenticated) {
      setIsApplyModalOpen(true);
      return;
    }
    setIsAuthOverlayOpen(true);
  };

  const handleAuthSuccess = () => {
    setIsAuthOverlayOpen(false);
    setIsUserAuthenticated(true);
    freshlyAuthenticatedRef.current = true;
    setIsApplyModalOpen(true);
  };

  const handleOverlayClose = () => {
    setIsAuthOverlayOpen(false);
    const token = getToken();
    if (token) {
      setIsUserAuthenticated(true);
      freshlyAuthenticatedRef.current = true;
      setIsApplyModalOpen(true);
    }
  };

  const navigateAfterApplication = async () => {
    try {
      const token = getToken();
      if (!token) {
        navigate('/dashboard');
        return;
      }
      let ack = false;
      let emailVerified = true;
      try {
        const decoded = jwtDecode(token);
        ack = decoded?.ack;
        emailVerified = decoded?.email_verified !== false;
        const cid = decoded?.cid;
        if (cid) await ResumeService.getProfile(cid).catch(() => {});
      } catch {
        /* ignore */
      }
      if (freshlyAuthenticatedRef.current && !emailVerified) {
        navigate('/create-link');
        return;
      }
      navigate(ack ? '/profile-dashboard' : '/dashboard');
    } catch {
      navigate('/dashboard');
    }
  };

  // ── Render states ───────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-5xl p-6 md:p-8">
        <div
          className="flex items-center justify-center gap-3 rounded-[var(--radius-md)] border p-10"
          style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
        >
          <Loader2
            className="h-5 w-5 animate-spin"
            style={{ color: 'var(--primary)' }}
            aria-hidden="true"
          />
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
            Loading opening details…
          </p>
        </div>
      </div>
    );
  }

  if (isError || !job) {
    return (
      <div className="mx-auto w-full max-w-5xl p-6 md:p-8">
        <div
          className="rounded-[var(--radius-md)] border p-5"
          style={{
            background: 'var(--destructive-light)',
            borderColor: 'var(--destructive)',
          }}
        >
          <p className="text-sm" style={{ color: 'var(--destructive)' }}>
            {error?.message || 'Unable to load opening details.'}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-3 inline-flex items-center gap-2 text-sm font-medium"
            style={{ color: 'var(--primary)' }}
          >
            <RefreshCcw size={14} aria-hidden="true" /> Retry
          </button>
        </div>
      </div>
    );
  }

  const requirementInfo = job?.requirement_info || {};
  const certBuckets = splitMandatoryItems(
    requirementInfo.certifications || [],
    'certification_name'
  );
  const additionalEntries = normalizeAdditionalDetails(job?.additional_details);
  const totalLocations = (job?.locations || []).length;

  return (
    <>
      {/* Auth overlay */}
      {isAuthOverlayOpen && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Sign in or create account"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleOverlayClose();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') handleOverlayClose();
          }}
          tabIndex={-1}
        >
          <div className="relative w-full max-w-md" role="presentation">
            <AuthCard
              onClose={handleOverlayClose}
              onAuthSuccess={handleAuthSuccess}
              defaultTab="Sign in"
            />
          </div>
        </div>
      )}

      {/* Apply modal */}
      <ApplyModal
        open={isApplyModalOpen}
        onOpenChange={setIsApplyModalOpen}
        job={job}
        onSuccess={navigateAfterApplication}
      />

      <div className="mx-auto w-full max-w-[1080px] px-7 pt-[22px] pb-24 text-left min-[980px]:pb-12">
        {/* Hero header — flat (.jd-top): border-bottom only, no card fill */}
        <div
          className="mb-6 border-b pb-5"
          style={{ borderColor: 'var(--border)' }}
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              {/* Status pill */}
              <span
                className="mt-1 mb-2 inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                style={{
                  background: 'var(--success-light)',
                  color: 'var(--success)',
                }}
              >
                <CheckCircle2 size={11} aria-hidden="true" />
                {job?.status || 'Open'}
              </span>
              <div className="flex flex-wrap items-start gap-3">
                <h1
                  className="text-2xl font-bold leading-tight"
                  style={{
                    fontFamily: 'var(--font-display)',
                    color: 'var(--text)',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {job?.title}
                </h1>
              </div>

              {/* Company line */}
              <div
                className="mt-2 text-[0.92rem] font-semibold"
                style={{ color: 'var(--text)' }}
              >
                {job?.company || 'Ahedge'}
              </div>

              {/* Quick meta */}
              <div
                className="mt-1.5 flex flex-wrap items-center gap-2 text-sm"
                style={{ color: 'var(--text-faint)' }}
              >
                <span>
                  {totalLocations}{' '}
                  {totalLocations === 1 ? 'location' : 'locations'}
                </span>
                {job?.job_code && (
                  <>
                    <span aria-hidden="true" style={{ opacity: 0.6 }}>
                      ·
                    </span>
                    <span>
                      Job Code:{' '}
                      <span className="tabular-nums">{job.job_code}</span>
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Two-column layout: flat content + sticky apply card */}
        <div className="grid items-start gap-6 min-[980px]:grid-cols-[1fr_300px] min-[980px]:gap-8">
          {/* ── Left: flat content sections ── */}
          <div className="flex flex-col gap-[22px]">
            {/* Job Summary */}
            <Section>
              <SectionHeading>Job Summary</SectionHeading>
              <JobDescriptionMarkdown md={job?.job_description} />

              {additionalEntries.length > 0 && (
                <div
                  className="mt-5 space-y-1 border-t pt-4"
                  style={{ borderColor: 'var(--border-light)' }}
                >
                  <h3
                    className="mb-2 text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--text-faint)' }}
                  >
                    Additional Details
                  </h3>
                  {additionalEntries.map((detail) => (
                    <p
                      key={`${detail.label}-${detail.value}`}
                      className="text-sm"
                      style={{ color: 'var(--text-muted)' }}
                    >
                      {detail.value}
                    </p>
                  ))}
                </div>
              )}

              {(requirementInfo.skills || []).length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {requirementInfo.skills.map((skill) => (
                    <span
                      key={skill.id || skill.skill_name}
                      className="rounded-full px-3 py-1 text-xs font-medium"
                      style={{
                        background: 'var(--primary-light)',
                        color: 'var(--primary)',
                        border: '1px solid rgba(68,114,196,0.18)',
                      }}
                    >
                      {skill.skill_name}
                    </span>
                  ))}
                </div>
              )}
            </Section>

            {/* Requirements — bulleted list (.jd-list) */}
            <Section>
              <SectionHeading>Requirements</SectionHeading>
              {flattenQualifications(requirementInfo.qualifications).length ? (
                <ul className="flex flex-col gap-2">
                  {flattenQualifications(requirementInfo.qualifications).map(
                    (row) => (
                      <li
                        key={row.key}
                        className="relative pl-[18px] text-sm leading-relaxed"
                        style={{ color: 'var(--text)' }}
                      >
                        <span
                          className="absolute left-0.5 top-[9px] h-1.5 w-1.5 rounded-full"
                          style={{ background: 'var(--primary)' }}
                          aria-hidden="true"
                        />
                        {row.label}
                        <span
                          className="ml-1.5 inline-block rounded-full px-[7px] py-px align-middle text-[0.66rem] font-semibold"
                          style={
                            row.mandatory
                              ? {
                                  background: 'var(--primary-light)',
                                  color: 'var(--primary)',
                                }
                              : {
                                  background: 'var(--surface-muted)',
                                  color: 'var(--text-faint)',
                                }
                          }
                        >
                          {row.mandatory ? 'Required' : 'Optional'}
                        </span>
                      </li>
                    )
                  )}
                </ul>
              ) : (
                <p
                  className="text-sm italic"
                  style={{ color: 'var(--text-faint)' }}
                >
                  No requirements listed.
                </p>
              )}
            </Section>

            {/* Certifications */}
            <Section>
              <SectionHeading>Certifications</SectionHeading>
              {certBuckets.mandatory.length || certBuckets.optional.length ? (
                <div className="flex flex-wrap gap-2">
                  {[...certBuckets.mandatory, ...certBuckets.optional]
                    .filter(Boolean)
                    .map((cert) => (
                      <span
                        key={cert}
                        className="inline-flex items-center gap-1.5 rounded-[var(--radius-sm)] px-3 py-1.5 text-sm font-medium"
                        style={{
                          background: 'var(--surface-muted)',
                          color: 'var(--text)',
                          border: '1px solid var(--border-light)',
                        }}
                      >
                        <Award
                          size={13}
                          aria-hidden="true"
                          style={{ color: 'var(--text-faint)' }}
                        />
                        {cert}
                      </span>
                    ))}
                </div>
              ) : (
                <p
                  className="text-sm italic"
                  style={{ color: 'var(--text-faint)' }}
                >
                  No certifications listed.
                </p>
              )}
            </Section>

            {/* Locations */}
            <Section>
              <SectionHeading>Locations</SectionHeading>
              {(job?.locations || []).length ? (
                <div className="flex flex-wrap gap-2">
                  {job.locations.map((loc) => (
                    <span
                      key={loc.requirement_location_id}
                      className="rounded-[var(--radius-sm)] px-3 py-1.5 text-sm font-medium"
                      style={{
                        background: 'var(--surface-muted)',
                        color: 'var(--text)',
                        border: '1px solid var(--border-light)',
                      }}
                    >
                      {loc.location_name}
                      {loc.employment_type ? ` · ${loc.employment_type}` : ''}
                    </span>
                  ))}
                </div>
              ) : (
                <p
                  className="text-sm italic"
                  style={{ color: 'var(--text-faint)' }}
                >
                  No locations listed.
                </p>
              )}
            </Section>
          </div>

          {/* ── Right: sticky apply card (the only card) ── */}
          <aside className="min-[980px]:sticky min-[980px]:top-16 min-[980px]:self-start">
            <div
              className="rounded-[var(--radius-lg)] border p-[18px]"
              style={{
                background: 'var(--surface)',
                borderColor: 'var(--border)',
                boxShadow: 'var(--shadow-sm)',
              }}
            >
              {/* Hidden on mobile — the fixed bottom bar carries Apply there */}
              <Button
                onClick={handleApplyClick}
                className="w-full hover:opacity-90 hidden min-[980px]:inline-flex"
                style={{
                  background: 'var(--primary)',
                  color: 'var(--primary-fg)',
                }}
              >
                Apply Now
              </Button>
              <dl className="mt-3.5">
                {[
                  {
                    label: 'Experience',
                    value: humanizeCareerLevel(requirementInfo.career_level),
                  },
                  {
                    label: 'Deadline',
                    value: formatDate(
                      job?.deadline || requirementInfo.closing_date
                    ),
                  },
                  {
                    label: 'Posted on',
                    value: formatDate(
                      requirementInfo.posted_on || job?.created_at
                    ),
                  },
                  ...(job?.job_code
                    ? [{ label: 'Job Code', value: job.job_code }]
                    : []),
                ].map((fact, idx) => (
                  <div
                    key={fact.label}
                    className="flex items-baseline justify-between gap-3 py-[9px]"
                    style={
                      idx === 0
                        ? undefined
                        : { borderTop: '1px solid var(--border-light)' }
                    }
                  >
                    <dt
                      className="text-[0.78rem]"
                      style={{ color: 'var(--text-faint)' }}
                    >
                      {fact.label}
                    </dt>
                    <dd
                      className="text-[0.82rem] font-semibold tabular-nums text-right"
                      style={{ color: 'var(--text)' }}
                    >
                      {fact.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </aside>
        </div>
      </div>

      {/* Mobile-only fixed Apply bar (desktop uses the sticky apply card) */}
      <div
        className="fixed inset-x-0 bottom-0 z-40 border-t px-4 py-3 min-[980px]:hidden"
        style={{
          background: 'var(--surface)',
          borderColor: 'var(--border)',
          boxShadow: '0 -2px 14px rgba(15,23,42,0.06)',
          paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))',
        }}
      >
        <Button
          onClick={handleApplyClick}
          className="w-full hover:opacity-90"
          style={{ background: 'var(--primary)', color: 'var(--primary-fg)' }}
        >
          Apply
        </Button>
      </div>
    </>
  );
}
